import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';

/**
 * EmptyState - slate icon, one sentence, one action (design.md §5.5).
 *
 * Replaces the bare "No X found" strings scattered through the admin tables and
 * dashboards. Never render "No data" on its own; say what is missing and give
 * the reader the next step where there is one.
 *
 * Props:
 *  - title       : the one sentence, e.g. "No properties yet"
 *  - description : optional supporting line
 *  - icon        : node; defaults to an inbox glyph
 *  - action      : { label, onClick, to, variant } or a node
 *  - dense       : tighter padding for use inside a table cell or a card
 */
const EmptyState = ({ title, description, icon, action, dense = false, sx }) => {
  const theme = useTheme();
  const { slate } = theme.palette.modern;

  const renderedAction = React.isValidElement(action)
    ? action
    : action?.label
      ? (
        <Button
          variant={action.variant || 'contained'}
          onClick={action.onClick}
          {...(action.to ? { href: action.to } : {})}
          startIcon={action.icon}
        >
          {action.label}
        </Button>
      )
      : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: dense ? 4 : { xs: 6, sm: 8 },
        px: 2,
        ...sx,
      }}
    >
      <Stack spacing={dense ? 1 : 1.5} alignItems="center" sx={{ maxWidth: 420 }}>
        <Box
          aria-hidden="true"
          sx={{ color: slate[400], display: 'flex', '& svg': { fontSize: dense ? 36 : 48 } }}
        >
          {icon || <InboxRoundedIcon />}
        </Box>

        <Typography variant={dense ? 'subtitle1' : 'h6'} sx={{ color: slate[800], fontWeight: 600 }}>
          {title}
        </Typography>

        {description && (
          <Typography variant="body2" sx={{ color: slate[600] }}>
            {description}
          </Typography>
        )}

        {renderedAction && <Box sx={{ pt: dense ? 0.5 : 1 }}>{renderedAction}</Box>}
      </Stack>
    </Box>
  );
};

export default EmptyState;
